import React, { useEffect, useRef, useState } from 'react';

import {
  CustomOverlayView,
  OVERLAY_MOUSE_TARGET,
} from '../../SearchPage/SearchMap/SearchMapWithGoogleMaps';
import MapCategoryPin from './MapCategoryPin';
import { SAUDI_CENTER, pinCategory } from './mapListings';

import css from './AlgoliaMap.module.css';

const DEFAULT_ZOOM = 11;

const LOCATE_ICON =
  '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" fill="currentColor"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" stroke-width="2"/></svg>';

/** Centre the label so the caret points at the coordinate. */
const getPixelPositionOffset = (width, height) => ({ x: -1 * (width / 2), y: -1 * (height + 3) });

export const isGoogleMapsLoaded = () =>
  typeof window !== 'undefined' && !!(window.google && window.google.maps);

/** The Maps script is injected globally and has no ready callback, so wait for it. */
export const useGoogleMapsReady = () => {
  const [ready, setReady] = useState(isGoogleMapsLoaded());
  useEffect(() => {
    if (ready) {
      return undefined;
    }
    const id = setInterval(() => {
      if (isGoogleMapsLoaded()) {
        setReady(true);
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, [ready]);
  return ready;
};

/**
 * Its own Google map (not ReusableMapContainer, which is a global singleton with
 * its own React root). Reports the visible bounds on `idle` as
 * {north, south, east, west}, and `onMoveStart` while the view changes.
 * "My location" is a native map control, so Google stacks it above the zoom
 * buttons instead of under them.
 */
const AlgoliaMap = props => {
  const {
    center,
    bounds,
    groups,
    selectedKey,
    categoryIndex,
    titleForGroup,
    onSelectGroup,
    onMapClick,
    onIdle,
    onMoveStart,
    onLocate,
    locateLabel,
    locating,
    onMapReady,
    className,
  } = props;
  const ready = useGoogleMapsReady();
  const containerRef = useRef(null);
  const [map, setMap] = useState(null);
  const locateButtonRef = useRef(null);

  // Handlers change every render; the map is created once.
  const handlers = useRef({});
  handlers.current = { onMapClick, onIdle, onMoveStart, onLocate };

  useEffect(() => {
    if (!ready || !containerRef.current) {
      return undefined;
    }
    const { maps } = window.google;
    const instance = new maps.Map(containerRef.current, {
      center: center || SAUDI_CENTER,
      zoom: DEFAULT_ZOOM,
      // The whole world can be browsed; listings just happen to be in Saudi Arabia.
      minZoom: 3,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      gestureHandling: 'greedy',
    });
    if (bounds) {
      instance.fitBounds(bounds);
    }
    instance.addListener('idle', () => {
      const b = instance.getBounds();
      if (!b) {
        return;
      }
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      handlers.current.onIdle({ north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() });
    });
    instance.addListener('bounds_changed', () => handlers.current.onMoveStart?.());
    instance.addListener('click', () => handlers.current.onMapClick());

    const locateButton = document.createElement('button');
    locateButton.type = 'button';
    locateButton.className = css.locateControl;
    locateButton.innerHTML = LOCATE_ICON;
    locateButton.addEventListener('click', () => handlers.current.onLocate?.());
    locateButtonRef.current = locateButton;
    instance.controls[maps.ControlPosition.RIGHT_BOTTOM].push(locateButton);
    setMap(instance);
    if (onMapReady) {
      onMapReady(instance);
    }
    return () => {
      maps.event.clearInstanceListeners(instance);
      locateButtonRef.current = null;
      setMap(null);
    };
    // The map is created once; moving it afterwards is done through the instance.
  }, [ready]);

  useEffect(() => {
    const button = locateButtonRef.current;
    if (button) {
      button.title = locateLabel || '';
      button.setAttribute('aria-label', locateLabel || '');
      button.disabled = !!locating;
      button.classList.toggle(css.locateControlBusy, !!locating);
    }
  }, [map, locateLabel, locating]);

  return (
    <div className={className}>
      <div ref={containerRef} className={css.map} />
      {map &&
        groups.map(group => (
          <CustomOverlayView
            key={group.key}
            map={map}
            mapPaneName={OVERLAY_MOUSE_TARGET}
            position={new window.google.maps.LatLng(group.coordinate.lat, group.coordinate.lng)}
            getPixelPositionOffset={getPixelPositionOffset}
          >
            <MapCategoryPin
              {...pinCategory(group, categoryIndex)}
              count={group.listings.length}
              title={titleForGroup ? titleForGroup(group) : undefined}
              isActive={group.key === selectedKey}
              onClick={() => onSelectGroup(group)}
            />
          </CustomOverlayView>
        ))}
    </div>
  );
};

export default AlgoliaMap;
